import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConfirmModal, LabeledFilterMultiSelect, LabeledFilterSelect } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useRemoveUserCollegeMutation,
  useUpdateUserCollegeMutation,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.api';
import {
  COLLEGE_TIERS,
  CollegeTier,
  UpdateUserCollegePayload,
  USER_COLLEGE_STATUSES,
  UserCollege,
  UserCollegeStatus,
  UserCollegesResponse,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, Link, SxProps, Theme, Typography } from '@mui/material';

import { MyCollegesCards } from './components/MyCollegesCards';
import { MyCollegesHeader } from './components/MyCollegesHeader';
import { MyCollegesTable } from './components/MyCollegesTable';
import { STATUS_TONES } from './components/StatusChip';
import { ViewToggle } from './components/ViewToggle';
import { useAgentContext } from './MyCollegesPage.agentContext';

const STRINGS = appStrings.modules.myColleges;
const ALL = '';

const STATUS_OPTIONS = USER_COLLEGE_STATUSES.map((status) => ({
  value: status,
  label: STRINGS.statusLabels[status],
}));

const LEVEL_OPTIONS = COLLEGE_TIERS.map((tier) => ({
  value: tier,
  label: STRINGS.tierLabels[tier],
}));

type MyCollegesView = 'table' | 'cards';

const VIEW_OPTIONS = [
  { value: 'table', label: STRINGS.views.table, icon: 'LayoutGrid02' },
  { value: 'cards', label: STRINGS.views.cards, icon: 'Grid01' },
] as const;

export const MyCollegesPage = ({ data }: Readonly<{ data: UserCollegesResponse }>) => {
  const [view, setView] = useState<MyCollegesView>('table');
  const [statusFilter, setStatusFilter] = useState<UserCollegeStatus | null>(null);
  const [fieldFilter, setFieldFilter] = useState<readonly string[]>(
    data.suggested_major_categories,
  );
  const [levelFilter, setLevelFilter] = useState<CollegeTier | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [removing, setRemoving] = useState<UserCollege | null>(null);
  const [updateUserCollege] = useUpdateUserCollegeMutation();
  const [removeUserCollege] = useRemoveUserCollegeMutation();

  const colleges = data.colleges.filter(
    (college) =>
      (!statusFilter || college.status === statusFilter) &&
      (!levelFilter || college.tier === levelFilter) &&
      (fieldFilter.length === 0 ||
        college.offered_major_categories.length === 0 ||
        college.offered_major_categories.some((code) => fieldFilter.includes(code))),
  );

  const fieldOptions = data.major_categories.map(({ code, name }) => ({
    value: code,
    label: name,
  }));

  useAgentContext({
    view,
    statusFilter,
    fieldFilter: data.major_categories
      .filter(({ code }) => fieldFilter.includes(code))
      .map(({ name }) => name),
    levelFilter,
    colleges,
  });

  const track = async (pending: Readonly<{ unwrap: () => Promise<unknown> }>) => {
    setSaveFailed(false);

    try {
      await pending.unwrap();
    } catch {
      setSaveFailed(true);
    }
  };

  const handleUpdate = (changes: UpdateUserCollegePayload) => track(updateUserCollege(changes));

  const handleRemove = (college: UserCollege) => {
    setRemoving(null);
    track(removeUserCollege(college.id));
  };

  const hasColleges = data.colleges.length > 0;

  return (
    <Box sx={pageSx}>
      <MyCollegesHeader colleges={data.colleges} />

      {hasColleges && (
        <Box sx={controlsSx}>
          <Box sx={filtersSx}>
            <Box sx={filterSx}>
              <LabeledFilterSelect
                id="my-colleges-status-filter"
                label={STRINGS.statusFilter.label}
                dotColor={statusFilter ? STATUS_TONES[statusFilter].dot : 'Brand/700'}
                placeholder={STRINGS.statusFilter.all}
                options={STATUS_OPTIONS}
                value={statusFilter ?? ALL}
                onChange={(value) =>
                  setStatusFilter(value === ALL ? null : (value as UserCollegeStatus))
                }
              />
            </Box>
            <Box sx={fieldFilterSx}>
              <LabeledFilterMultiSelect
                id="my-colleges-field-filter"
                label={STRINGS.fieldFilter.label}
                placeholder={STRINGS.fieldFilter.all}
                options={fieldOptions}
                values={fieldFilter}
                onChange={setFieldFilter}
                selectedCountLabel={STRINGS.fieldFilter.selected}
              />
            </Box>
            <Box sx={filterSx}>
              <LabeledFilterSelect
                id="my-colleges-level-filter"
                label={STRINGS.levelFilter.label}
                placeholder={STRINGS.levelFilter.all}
                options={LEVEL_OPTIONS}
                value={levelFilter ?? ALL}
                onChange={(value) => setLevelFilter(value === ALL ? null : (value as CollegeTier))}
              />
            </Box>
          </Box>
          <ViewToggle
            id="my-colleges-view"
            options={VIEW_OPTIONS}
            value={view}
            onChange={setView}
          />
        </Box>
      )}

      {hasColleges && !data.has_scores && (
        <Box id="my-colleges-add-score-hint" sx={hintSx}>
          <Typography variant="text-sm-regular">{STRINGS.addScoreHint}</Typography>
          <Link
            id="my-colleges-add-score-link"
            component={RouterLink}
            to={`${APP_ROUTE.PROFILE}?tab=Academics`}
            variant="text-sm-semibold"
          >
            {STRINGS.addScoreLink}
          </Link>
        </Box>
      )}

      {!hasColleges && (
        <Box id="my-colleges-empty" sx={emptySx}>
          <Typography variant="text-lg-semibold">{STRINGS.empty.title}</Typography>
          <Typography variant="text-md-regular" sx={mutedSx}>
            {STRINGS.empty.description}
          </Typography>
        </Box>
      )}

      {saveFailed && (
        <Typography id="my-colleges-save-failed" variant="text-sm-medium" sx={errorSx}>
          {STRINGS.saveFailed}
        </Typography>
      )}

      {hasColleges && colleges.length === 0 && (
        <Typography variant="text-md-regular" sx={mutedSx}>
          {STRINGS.noMatches}
        </Typography>
      )}

      {colleges.length > 0 &&
        (view === 'table' ? (
          <MyCollegesTable
            colleges={colleges}
            hasFamilyIncome={data.has_family_income}
            onStatusChange={(college, status) => handleUpdate({ id: college.id, status })}
            onPlanChange={(college, plan) =>
              handleUpdate({ id: college.id, application_plan: plan })
            }
            onRemove={setRemoving}
          />
        ) : (
          <MyCollegesCards
            colleges={colleges}
            hasFamilyIncome={data.has_family_income}
            onStatusChange={(college, status) => handleUpdate({ id: college.id, status })}
            onPlanChange={(college, plan) =>
              handleUpdate({ id: college.id, application_plan: plan })
            }
            onRemove={setRemoving}
          />
        ))}

      {removing && (
        <ConfirmModal
          open
          onClose={() => setRemoving(null)}
          headerText={STRINGS.removeDialog.title}
          bodyText={STRINGS.removeDialog.body(removing.name)}
          primaryAction={{
            id: 'my-colleges-remove-confirm',
            label: STRINGS.removeDialog.confirm,
            onClick: () => handleRemove(removing),
          }}
          secondaryAction={{
            id: 'my-colleges-remove-cancel',
            label: STRINGS.removeDialog.cancel,
            onClick: () => setRemoving(null),
          }}
        />
      )}
    </Box>
  );
};

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  gap: backrs.spacing['spacing-2xl'],
  paddingBottom: backrs.spacing['spacing-4xl'],
});

const controlsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-xl'],
});

const filtersSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-lg'],
});

const filterSx: SxProps<Theme> = {
  width: '127px',
};

const fieldFilterSx: SxProps<Theme> = {
  width: '144px',
};

const hintSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-lg'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const emptySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: backrs.spacing['spacing-sm'],
  paddingBlock: backrs.spacing['spacing-6xl'],
});

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
